// Private Veranstaltungen: Anfragen, Zusagen, Gästeliste, Einladungen.
//
// Die Seite beantwortet drei Fragen, in dieser Reihenfolge:
//   1. Wer hat angefragt, und sagen wir zu oder ab?
//   2. Wer kommt, und wie kommen die Gäste an den Ton?
//   3. Was machen wir hinterher damit – Mitschnitt und Playliste?
//
// Bezahlt wird nicht hier. Was vereinbart ist, steht als Notiz am Termin.

import { useCallback, useEffect, useState } from "react";
import {
  veranstaltungApi,
  type FestBeitrag,
  type FestEinladung,
  type FestEintrag,
  type FestUebersicht,
} from "../api";
import { api } from "../api";
import { errorText } from "../components/streaming-ui";
import { Initialen, ModulAus, Sym } from "../components/social-ui";
import { Badge, Callout, Card, EmptyState, Page, PageHeader, Skeleton, Tabs } from "../components/ui";

const STATUS_TEXT: Record<FestEintrag["status"], string> = {
  angefragt: "Angefragt",
  bestaetigt: "Bestätigt",
  abgesagt: "Abgesagt",
};

const STATUS_TON: Record<FestEintrag["status"], "warning" | "success" | "neutral"> = {
  angefragt: "warning",
  bestaetigt: "success",
  abgesagt: "neutral",
};

function mb(bytes: number): string {
  return bytes >= 1_000_000 ? `${Math.round(bytes / 1_000_000)} MB` : `${Math.round(bytes / 1000)} kB`;
}

/** „Samstag, 3. Oktober, 19 Uhr bis 1 Uhr“ – so, wie es in der Suite gelesen wird. */
function wann(f: FestEintrag): string {
  return `${f.day_label}, ${f.time_label} bis ${f.end_label}`;
}

/** Datum und Uhrzeit für die Eingabefelder, in der Zeitzone des Senders. */
function felder(f: FestEintrag, zone: string): { date: string; time: string; end_time: string } {
  const tag = (at: string): string => new Intl.DateTimeFormat("sv-SE", { timeZone: zone }).format(new Date(at));
  const uhr = (at: string): string =>
    new Intl.DateTimeFormat("de-DE", { timeZone: zone, hour: "2-digit", minute: "2-digit" }).format(new Date(at));
  return { date: tag(f.starts_at), time: uhr(f.starts_at), end_time: uhr(f.ends_at) };
}

/** Tag und Monat als kleines Kalenderblatt – in der Zeitzone des Senders. */
function Kalenderblatt({ at, zone, blass = false }: { at: string; zone: string; blass?: boolean }): JSX.Element {
  const d = new Date(at);
  const tag = new Intl.DateTimeFormat("de-DE", { timeZone: zone, day: "numeric" }).format(d);
  const monat = new Intl.DateTimeFormat("de-DE", { timeZone: zone, month: "short" }).format(d).replace(".", "");
  return (
    <span className={`flex h-12 w-12 flex-shrink-0 flex-col items-center justify-center overflow-hidden rounded-lg border border-default bg-card leading-none ${blass ? "opacity-60" : ""}`}>
      <span className="w-full bg-brand-primary/10 py-0.5 text-center text-[0.6rem] font-semibold uppercase tracking-wider text-brand-primary">{monat}</span>
      <span className="flex-1 pt-1 text-lg font-semibold tabular-nums text-primary">{tag}</span>
    </span>
  );
}

/** Die Gästeliste einer Feier: Einladungen erzeugen, verschicken, zurückziehen. */
function Gaeste({ fest, darfAendern, melde }: { fest: FestEintrag; darfAendern: boolean; melde: (t: string) => void }): JSX.Element {
  const [invites, setInvites] = useState<FestEinladung[]>([]);
  const [page, setPage] = useState("");
  const [anzahl, setAnzahl] = useState(10);
  const [namen, setNamen] = useState("");
  const [qr, setQr] = useState<{ id: string; svg: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const laden = useCallback(async () => {
    try {
      const d = await veranstaltungApi.einladungen(fest.id);
      setInvites(d.invites);
      setPage(d.page);
    } catch (err) {
      melde(errorText(err));
    }
  }, [fest.id, melde]);
  useEffect(() => void laden(), [laden]);

  const erzeugen = async (): Promise<void> => {
    setBusy(true);
    try {
      // Eine Zeile je Gast: „Name; E-Mail oder Telefon“. Wer nur Zettel auslegen
      // will, lässt das Feld leer und nimmt die Anzahl.
      const guests = namen
        .split("\n")
        .map((z) => z.trim())
        .filter((z) => z !== "")
        .map((z) => {
          const [name, contact] = z.split(";");
          return { name: (name ?? "").trim().slice(0, 80), contact: (contact ?? "").trim().slice(0, 160) };
        });
      const d = await veranstaltungApi.einladen(fest.id, { guests, count: guests.length > 0 ? 0 : anzahl });
      melde(`${d.created} Einladung(en) erzeugt.`);
      setNamen("");
      await laden();
    } catch (err) {
      melde(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const zeigeQr = async (e: FestEinladung): Promise<void> => {
    if (qr?.id === e.id) {
      setQr(null);
      return;
    }
    try {
      setQr({ id: e.id, svg: await veranstaltungApi.qrSvg(fest.id, e.id) });
    } catch (err) {
      melde(errorText(err));
    }
  };

  const aktiv = invites.filter((e) => !e.revoked).length;

  return (
    <div className="space-y-3">
      <p className="text-sm leading-relaxed text-muted">
        Je Gast ein eigener Schlüssel. Ein Passwort für alle steht nach fünf Minuten in einer Nachrichtengruppe, und man
        kann niemanden mehr einzeln aussperren. Jeder Zugang gilt nur im Zeitfenster der Feier und auf
        höchstens {fest.device_limit} Gerät(en) gleichzeitig.
      </p>
      {page && (
        <p className="well flex min-w-0 items-center gap-2 text-sm">
          <Sym name="link" className="h-4 w-4 text-muted" />
          <span className="text-secondary">Gästeseite:</span>
          <a className="min-w-0 truncate font-medium text-brand-primary underline-offset-2 hover:underline" href={page} target="_blank" rel="noreferrer">{page}</a>
        </p>
      )}

      {darfAendern && (
        <div className="grid gap-4 rounded-lg border border-default p-4 sm:grid-cols-[minmax(0,1fr)_14rem]">
          <label className="block text-[0.8rem] font-medium text-secondary">
            Gästeliste (eine Zeile je Gast: <em>Name; E-Mail oder Telefon</em>)
            <textarea
              className="input mt-1.5 font-mono text-xs leading-relaxed"
              rows={4}
              value={namen}
              onChange={(e) => setNamen(e.target.value)}
              placeholder={"Anna Meier; anna@example.org\nBernd Schmitz; 0170 1234567"}
            />
          </label>
          <label className="flex flex-col text-[0.8rem] font-medium text-secondary">
            Oder einfach so viele Zettel zum Auslegen
            <input
              className="input mt-1.5"
              type="number"
              min={1}
              max={500}
              value={anzahl}
              onChange={(e) => setAnzahl(Number(e.target.value) || 1)}
            />
            <button
              className="btn btn-primary mt-auto w-full"
              style={{ marginTop: "auto" }}
              onClick={() => void erzeugen()}
              disabled={busy}
            >
              Einladungen erzeugen
            </button>
          </label>
        </div>
      )}

      <div className="flex items-center gap-2 pt-1">
        <span className="section-label">Einladungen</span>
        <span className="text-xs text-muted">{`${invites.length} Einladung(en), davon ${aktiv} gültig.`}</span>
      </div>
      <ul className="divide-y divide-default overflow-hidden rounded-lg border border-default text-sm">
        {invites.length === 0 && <li className="px-3 py-3 text-muted">Noch keine Einladungen.</li>}
        {invites.map((e) => (
          <li key={e.id} className={`px-3 py-2.5 ${e.revoked ? "opacity-60" : ""}`}>
            <div className="flex flex-wrap items-center gap-2">
              <Initialen name={e.guest_name || "?"} className="h-7 w-7 text-[0.7rem]" />
              <span className="font-medium text-primary">{e.guest_name || "ohne Namen"}</span>
              {e.guest_contact && <span className="text-muted">{e.guest_contact}</span>}
              <span className="rounded bg-input px-1.5 py-0.5 font-mono text-[11px] text-secondary" style={{ boxShadow: "inset 0 0 0 1px var(--border)" }}>…{e.key_hint}</span>
              {e.revoked && <Badge>zurückgezogen</Badge>}
              {e.devices_now > 0 && <Badge tone="success" dot>{`hört gerade (${e.devices_now})`}</Badge>}
              {e.sent_at && <Badge tone="info">verschickt</Badge>}
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-1 pl-9 text-xs">
              <button className="btn btn-ghost btn-sm" onClick={() => void navigator.clipboard.writeText(e.link).then(() => melde("Link kopiert."))}>
                <Sym name="copy" className="h-3.5 w-3.5" />Link kopieren
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => void zeigeQr(e)}>
                {qr?.id === e.id ? "QR-Code zuklappen" : "QR-Code zum Ausdrucken"}
              </button>
              {darfAendern && !e.sent_at && (
                <button className="btn btn-ghost btn-sm" onClick={() => void veranstaltungApi.verschickt(fest.id, e.id).then(laden)}>
                  <Sym name="send" className="h-3.5 w-3.5" />als verschickt merken
                </button>
              )}
              {darfAendern && !e.revoked && (
                <button
                  className="btn btn-ghost btn-sm !text-red-600 dark:!text-red-400"
                  onClick={() => {
                    if (!window.confirm(`Zugang von „${e.guest_name || "diesem Gast"}“ zurückziehen?`)) return;
                    void veranstaltungApi.zurueckziehen(fest.id, e.id).then((r) => {
                      melde(r.message);
                      return laden();
                    });
                  }}
                >
                  zurückziehen
                </button>
              )}
            </div>
            {qr?.id === e.id && (
              <div className="mt-2 flex flex-wrap items-end gap-3 pl-9">
                {/* Der QR-Code kommt als SVG von der API – kein fremder Dienst, keine Bilddatei im Netz. */}
                <div className="inline-block w-40 rounded-lg bg-white p-2 shadow-sm [&>svg]:h-auto [&>svg]:w-full" dangerouslySetInnerHTML={{ __html: qr.svg }} />
                <p className="max-w-xs text-xs text-muted">Ausdrucken und auf den Tisch legen. Wer ihn abfotografiert, ist drin – also nicht öffentlich aushängen.</p>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Hinterher: was lief, und was der Gastgeber mitnehmen darf. */
function Danach({ fest, darfAendern, melde }: { fest: FestEintrag; darfAendern: boolean; melde: (t: string) => void }): JSX.Element {
  const [stunden, setStunden] = useState<Array<{ id: string; label: string; bytes: number }>>([]);
  const [geschenk, setGeschenk] = useState<string | null>(null);
  const [bis, setBis] = useState<string | null>(fest.gift_until);

  useEffect(() => {
    veranstaltungApi.mitschnitt(fest.id)
      .then((d) => {
        setStunden(d.hours);
        setBis(d.gift_until);
      })
      .catch((err) => melde(errorText(err)));
  }, [fest.id, melde]);

  return (
    <div className="space-y-5 text-sm">
      <Callout tone="info">
        Eine private Feier ist keine Privatsache, sobald gesendet wird: Die Veranstaltung ist bei der GEMA zu melden.
        Die Playliste hier nimmt euch die Schreibarbeit ab – die Meldung selbst nicht.
      </Callout>
      <button
        className="btn btn-primary"
        onClick={() => void veranstaltungApi.playliste(fest.id, fest.slug).catch((err) => melde(errorText(err)))}
      >
        <Sym name="download" />Playliste des Abends herunterladen
      </button>

      <div>
        <h3 className="section-label mb-2">Mitschnitt</h3>
        {stunden.length === 0 ? (
          <p className="text-muted">
            Für dieses Zeitfenster liegt kein Mitschnitt vor. Der Mitschnitt muss für den Festkanal eingeschaltet
            sein – das geht unter „Mitschnitt“.
          </p>
        ) : (
          <ul className="divide-y divide-default overflow-hidden rounded-lg border border-default">
            {stunden.map((h) => (
              <li key={h.id} className="flex items-center gap-2 px-3 py-2 text-secondary">
                <Sym name="headphones" className="h-4 w-4 text-muted" />
                <span className="flex-1">{h.label}</span>
                <span className="tabular-nums text-xs text-muted">{mb(h.bytes)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {darfAendern && (
        <div className="space-y-2">
          <h3 className="section-label">Für den Gastgeber</h3>
          <div className="flex flex-wrap gap-2">
          <button
            className="btn btn-secondary"
            onClick={() =>
              void veranstaltungApi.geschenk(fest.id)
                .then((d) => {
                  setGeschenk(d.url);
                  melde(`Abholzugang für ${d.days} Tage erzeugt.`);
                })
                .catch((err) => melde(errorText(err)))
            }
          >
            {bis || geschenk ? "Abholzugang erneuern" : "Abholzugang für den Gastgeber erzeugen"}
          </button>
          {(bis || geschenk) && (
            <button
              className="btn btn-ghost !text-red-600 dark:!text-red-400"
              onClick={() =>
                void veranstaltungApi.geschenkWeg(fest.id).then(() => {
                  setGeschenk(null);
                  setBis(null);
                  melde("Abholzugang zurückgezogen.");
                })
              }
            >
              zurückziehen
            </button>
          )}
          </div>
          {geschenk && (
            <p className="well break-all font-mono text-xs text-primary">
              {geschenk}
              <button className="btn btn-secondary btn-sm ml-2 font-sans" onClick={() => void navigator.clipboard.writeText(geschenk).then(() => melde("Link kopiert."))}>
                kopieren
              </button>
            </p>
          )}
          {bis && !geschenk && <p className="text-muted">{`Ein Abholzugang gilt noch bis ${new Date(bis).toLocaleDateString("de-DE")}.`}</p>}
        </div>
      )}
    </div>
  );
}

/** Was die Gäste geschrieben haben – Grüße und Musikwünsche, nur aus diesem Raum. */
function Raum({ fest, melde }: { fest: FestEintrag; melde: (t: string) => void }): JSX.Element {
  const [liste, setListe] = useState<FestBeitrag[]>([]);
  useEffect(() => {
    const laden = (): void => {
      veranstaltungApi.beitraege(fest.id).then(setListe).catch((err) => melde(errorText(err)));
    };
    laden();
    const takt = setInterval(laden, 15_000);
    return () => clearInterval(takt);
  }, [fest.id, melde]);

  if (liste.length === 0) {
    return <EmptyState icon={<Sym name="chat" className="h-5 w-5" />} title="Noch nichts von den Gästen." />;
  }
  return (
    <ul className="space-y-2 text-sm">
      {liste.map((b) => (
        <li key={b.id} className="flex gap-3 rounded-lg border border-default p-3">
          <Initialen name={b.name || "Gast"} className="h-8 w-8 text-xs" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium text-primary">{b.name || "Gast"}</span>
              <Badge tone={b.kind === "wunsch" ? "info" : "neutral"}>
                {b.kind === "wunsch" ? <><Sym name="music" className="h-3 w-3" />Wunsch</> : "Gruß"}
              </Badge>
              <span className="text-xs text-muted">{b.time_label}</span>
            </div>
            <div className="mt-0.5 text-secondary">{b.text}</div>
          </div>
        </li>
      ))}
    </ul>
  );
}

export function VeranstaltungPage(): JSX.Element {
  const [on, setOn] = useState<boolean | null>(null);
  const [data, setData] = useState<FestUebersicht | null>(null);
  const [offen, setOffen] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [grund, setGrund] = useState("");
  const [notiz, setNotiz] = useState("");
  // Bei bestätigten Feiern stehen die drei Arbeitsschritte in Reitern.
  const [reiter, setReiter] = useState<"termin" | "gaeste" | "raum" | "danach">("termin");

  const melde = useCallback((t: string) => setHinweis(t), []);

  const laden = useCallback(async () => {
    try {
      setData(await veranstaltungApi.uebersicht());
    } catch (err) {
      melde(errorText(err));
    }
  }, [melde]);

  useEffect(() => {
    api.listModules()
      .then((list) => setOn(list.some((m) => m.id === "veranstaltung" && m.enabled)))
      .catch(() => setOn(false));
  }, []);
  useEffect(() => {
    if (on) void laden();
  }, [on, laden]);

  if (on === false) {
    return (
      <ModulAus title="Private Veranstaltungen" icon="party">
        „Private Veranstaltungen“ ist nicht eingeschaltet. Das geht unter Aussehen → Mitmach-Module.
      </ModulAus>
    );
  }
  if (!data) {
    return (
      <Page className="max-w-6xl">
        <PageHeader eyebrow="Verwalten" title="Private Veranstaltungen" />
        {hinweis ? <Callout tone="danger">{hinweis}</Callout> : (
          <div className="grid gap-5 lg:grid-cols-[22rem_minmax(0,1fr)]" aria-label="Lädt …">
            <Skeleton className="h-64" />
            <Skeleton className="h-64" />
          </div>
        )}
      </Page>
    );
  }

  const fest = data.events.find((f) => f.id === offen) ?? null;

  const aktion = async (f: FestEintrag, action: "bestaetigen" | "absagen"): Promise<void> => {
    if (action === "absagen" && grund.trim() === "") {
      melde("Bitte kurz sagen, warum die Feier nicht geht.");
      return;
    }
    try {
      await veranstaltungApi.aktion(f.id, { action, reason: grund.trim() || undefined, note: notiz.trim() || undefined });
      setGrund("");
      setNotiz("");
      melde(action === "bestaetigen" ? "Zugesagt. Jetzt die Gästeliste anlegen." : "Abgesagt. Alle Einladungen sind zurückgezogen.");
      await laden();
    } catch (err) {
      melde(errorText(err));
    }
  };

  const oeffnen = (id: string): void => {
    setOffen(offen === id ? null : id);
    setReiter("termin");
  };

  const angaben: Array<[string, React.ReactNode, boolean?]> = fest ? [
    ["Wann", wann(fest)],
    ["Art", fest.kind || "—"],
    ["Gäste", fest.guests_expected || "—"],
    ["Kontakt", [fest.contact_name, fest.contact_mail, fest.contact_phone].filter(Boolean).join(" · ") || "—"],
    ["Wünsche", fest.wishes || "—", true],
    ["Zugang gilt", <>
      {`${new Date(fest.access_from).toLocaleString("de-DE")} bis ${new Date(fest.access_to).toLocaleString("de-DE")}`}
      <span className="text-muted">{` (${fest.lead_minutes} Min. Vorlauf, ${fest.trail_minutes} Min. Nachlauf)`}</span>
    </>],
    ["Vereinbart", fest.note || "—", true],
    ...(fest.reject_reason ? [["Absagegrund", fest.reject_reason] as [string, React.ReactNode]] : []),
  ] : [];

  const termin = fest && (
    <>
      <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-[9rem_1fr]">
        {angaben.map(([k, v, pre]) => (
          <div key={k} className="contents">
            <dt className="text-muted">{k}</dt>
            <dd className={`text-primary ${pre ? "whitespace-pre-wrap" : ""}`}>{v}</dd>
          </div>
        ))}
      </dl>

      {data.may_edit && fest.status !== "abgesagt" && (
        <div className="mt-5 space-y-4 border-t border-default pt-5 text-sm">
          <label className="block text-[0.8rem] font-medium text-secondary">
            Notiz zum Vereinbarten (Preis, Anzahlung, Absprachen – nur für euch)
            <textarea
              className="input mt-1.5"
              rows={2}
              value={notiz}
              onChange={(e) => setNotiz(e.target.value)}
              placeholder={fest.note}
            />
          </label>
          <label className="block text-[0.8rem] font-medium text-secondary">
            Bei einer Absage: Warum? (steht später im Eingang)
            <input
              className="input mt-1.5"
              value={grund}
              onChange={(e) => setGrund(e.target.value)}
            />
          </label>
          <div className="flex flex-wrap gap-2">
            {fest.status === "angefragt" && (
              <button className="btn btn-primary" onClick={() => void aktion(fest, "bestaetigen")}>
                <Sym name="check" />Zusagen
              </button>
            )}
            <button className="btn btn-secondary !text-red-600 dark:!text-red-400" onClick={() => void aktion(fest, "absagen")}>
              Absagen
            </button>
          </div>
          <p className="text-xs text-muted">
            Die Nachricht an den Kontakt schreibt ihr selbst – per Mail oder am Telefon. Ein Automat trifft bei so
            etwas selten den richtigen Ton.
          </p>
        </div>
      )}
    </>
  );

  return (
    <Page className="max-w-6xl">
      <PageHeader
        eyebrow="Verwalten"
        title="Private Veranstaltungen"
        description="Jemand bucht einen Termin – Hochzeit, Geburtstag, Vereinsfest – und nur seine Gäste hören mit. Anfrage und Zusage laufen von Hand; bezahlt wird außerhalb der Suite."
        actions={data.channel ? <Badge tone="neutral"><Sym name="radio" className="h-3 w-3" />{data.channel.name}</Badge> : undefined}
      />

      {hinweis && (
        <div className="callout callout-info mb-4 items-center">
          <Sym name="bell" className="h-4 w-4" />
          <div className="min-w-0 flex-1">{hinweis}</div>
          <button type="button" onClick={() => setHinweis(null)} className="rounded p-0.5 opacity-70 hover:opacity-100" style={{ minHeight: 0 }} aria-label="Hinweis schließen">
            <Sym name="x" className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {!data.channel && (
        <div className="callout callout-warning mb-5 flex-wrap items-center">
          <Sym name="alert" className="h-4 w-4" />
          <div className="min-w-0 flex-1">Es ist noch kein Kanal für Feiern eingestellt. Ohne den kann niemand zuhören.</div>
          {data.is_admin && (
            <button
              className="btn btn-primary btn-sm"
              onClick={() =>
                void veranstaltungApi.kanalAnlegen("Festsaal")
                  .then((d) => melde(d.message))
                  .catch((err) => melde(errorText(err)))
              }
            >
              Festkanal anlegen
            </button>
          )}
        </div>
      )}

      <div className="grid items-start gap-5 lg:grid-cols-[22rem_minmax(0,1fr)]">
        <Card
          padded={false}
          className="min-w-0"
          title={data.waiting > 0 ? `Anfragen (${data.waiting} offen)` : "Anfragen und Termine"}
          actions={data.waiting > 0 ? <Badge tone="warning" dot>{data.waiting} offen</Badge> : undefined}
        >
          {data.events.length === 0 ? (
            <div className="p-4">
              <EmptyState icon={<Sym name="party" className="h-5 w-5" />} title="Noch keine Anfrage."
                description="Der Block „Feier anfragen“ gehört dafür auf die Hörerseite." />
            </div>
          ) : (
            <ul className="divide-y divide-default">
              {data.events.map((f) => (
                <li key={f.id}>
                  <button className={`flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-surface-hover ${f.id === offen ? "bg-brand-primary/[0.06]" : ""}`}
                    style={f.id === offen ? { boxShadow: "inset 3px 0 0 var(--brand-primary)" } : undefined}
                    onClick={() => oeffnen(f.id)}>
                    <Kalenderblatt at={f.starts_at} zone={data.time_zone} blass={f.status === "abgesagt"} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-primary">{f.title}</span>
                      <span className="block truncate text-xs text-muted">{f.time_label} bis {f.end_label}{f.guests_expected > 0 ? ` · ${f.guests_expected} Gäste` : ""}</span>
                      <span className="mt-1 block"><Badge tone={STATUS_TON[f.status]} dot>{STATUS_TEXT[f.status]}</Badge></span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {fest ? (
          <section className="card min-w-0 overflow-hidden">
            <div className="flex flex-wrap items-start gap-4 border-b border-default p-5">
              <Kalenderblatt at={fest.starts_at} zone={data.time_zone} />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-lg font-semibold text-primary">{fest.title}</h2>
                  <Badge tone={STATUS_TON[fest.status]} dot>{STATUS_TEXT[fest.status]}</Badge>
                </div>
                <p className="mt-0.5 text-sm text-muted">{wann(fest)}</p>
              </div>
              <button className="btn btn-ghost btn-sm btn-icon" onClick={() => setOffen(null)} aria-label="Schließen" title="Schließen"><Sym name="x" /></button>
            </div>
            {fest.status === "bestaetigt" && (
              <div className="border-b border-default px-5 py-3">
                <Tabs value={reiter} onChange={setReiter} items={[
                  { value: "termin", label: "Termin" },
                  { value: "gaeste", label: "Gästeliste und Einladungen" },
                  { value: "raum", label: "Aus dem Raum" },
                  { value: "danach", label: "Hinterher" },
                ]} />
              </div>
            )}
            <div className="p-5">
              {(fest.status !== "bestaetigt" || reiter === "termin") && termin}
              {fest.status === "bestaetigt" && reiter === "gaeste" && <Gaeste fest={fest} darfAendern={data.may_edit} melde={melde} />}
              {fest.status === "bestaetigt" && reiter === "raum" && <Raum fest={fest} melde={melde} />}
              {fest.status === "bestaetigt" && reiter === "danach" && <Danach fest={fest} darfAendern={data.may_edit} melde={melde} />}
            </div>
          </section>
        ) : (
          <div className="card hidden p-2 lg:block">
            <EmptyState icon={<Sym name="calendar" className="h-5 w-5" />} title="Einen Termin wählen"
              description="Links eine Anfrage anklicken – hier stehen dann Angaben, Zusage und Gästeliste." />
          </div>
        )}
      </div>
    </Page>
  );
}
